import React from "react";
import { Link } from "react-router-dom";
import { InView, EyebrowLabel, PageHero, InlineCta } from "../utils/Animation";
import {
  SERVICES_REGISTRY,
  type ServiceCard,
} from "../data/services/servicesRegistry";

// ─── Process steps (static — universal to all services) ──────────────
const PROCESS = [
  {
    step: "01",
    title: "Discovery",
    body: "We start by deeply understanding your goals, users, and constraints. No assumptions — just clear alignment.",
  },
  {
    step: "02",
    title: "Architecture",
    body: "We design a scalable technical foundation before writing the first line of code. Changing structure later is expensive.",
  },
  {
    step: "03",
    title: "Sprint Delivery",
    body: "Two-week sprints with working demos at every checkpoint. You see progress constantly, never in the dark.",
  },
  {
    step: "04",
    title: "Launch & Iterate",
    body: "We ship, monitor, and iterate. The work doesn't end at go-live — it begins.",
  },
];

// ─── Service row ──────────────────────────────────────────────────────
function ServiceRow({
  service,
  delay,
}: {
  service: ServiceCard;
  delay: number;
}) {
  return (
    <InView delay={delay}>
      <div className="py-16 grid grid-cols-1 lg:grid-cols-[1fr_1.8fr] gap-12 items-start group">
        {/* Left */}
        <div>
          <div className="flex items-start gap-5 mb-6">
            <span className="font-mono text-[11px] text-gray-300 mt-1">
              {service.number}
            </span>
            <h2 className="font-serif text-3xl lg:text-4xl font-light text-itl-navy leading-snug">
              {service.title}
            </h2>
          </div>
          <p className="text-itl-blue text-[13px] font-medium mb-4 pl-10">
            {service.tagline}
          </p>
          <Link
            to={service.href}
            className="inline-flex items-center gap-2 ml-10 text-[11px] font-semibold uppercase tracking-widest text-itl-navy border-b border-itl-navy hover:text-itl-blue hover:border-itl-blue pb-0.5 transition-colors group/link"
          >
            Explore service
            <span className="group-hover/link:translate-x-1 transition-transform inline-block">
              →
            </span>
          </Link>
        </div>

        {/* Right */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-400 mb-4">
              What We Deliver
            </p>
            <ul className="flex flex-col gap-2.5">
              {service.deliverables.map((d) => (
                <li
                  key={d}
                  className="flex items-start gap-2.5 text-[13px] text-gray-600 leading-snug"
                >
                  <span className="mt-1.5 w-1 h-1 bg-itl-blue rounded-full shrink-0" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-400 mb-4">
              Tech Stack
            </p>
            <div className="flex flex-wrap gap-2">
              {service.techStack.map((t) => (
                <span
                  key={t}
                  className="px-3 py-1 border border-gray-200 text-gray-500 text-[11px] uppercase tracking-wide"
                >
                  {t}
                </span>
              ))}
            </div>
            <div className="mt-8 relative overflow-hidden aspect-video">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-itl-navy/10" />
            </div>
          </div>
        </div>
      </div>
    </InView>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────
export default function Services() {
  return (
    <>
      <PageHero
        eyebrow="What We Do"
        title={
          <>
            Let's turn your biggest opportunities
            <br />
            <em className="italic">into your next big moves.</em>
          </>
        }
        subtitle="From pixel-perfect frontends to battle-tested backends — we build digital products that work, scale, and deliver real business value."
        image="/images/hero.png"
      />

      {/* ── Services list ── */}
      {/* SERVICES_REGISTRY drives this list — add a JSON + registry entry to add a service */}
      <section className="bg-white py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col divide-y divide-gray-100">
            {SERVICES_REGISTRY.map((service, i) => (
              <ServiceRow
                key={service.slug}
                service={service}
                delay={i * 0.05}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── Our Process ── */}
      <section className="bg-[#f7f7f5] py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <InView className="mb-16">
            <EyebrowLabel>How We Work</EyebrowLabel>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-itl-navy max-w-lg leading-snug">
              A process built around delivering — not just planning.
            </h2>
          </InView>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200">
            {PROCESS.map((p, i) => (
              <InView key={p.step} delay={i * 0.08} className="bg-white p-8">
                <span className="font-mono text-[11px] text-gray-300 block mb-6">
                  {p.step}
                </span>
                <h3 className="font-serif text-2xl font-light text-itl-navy mb-4">
                  {p.title}
                </h3>
                <p className="text-gray-500 text-[13px] leading-relaxed">
                  {p.body}
                </p>
              </InView>
            ))}
          </div>
        </div>
      </section>

      <InlineCta
        heading="Every great product starts with a clear brief."
        body="Tell us what you're building and we'll show you how we'd approach it — no obligation."
        ctaLabel="Start a Project"
        ctaHref="/contact"
      />
    </>
  );
}
